import React, { useState } from 'react';
import { ArrowRight, Shield, Wifi, Server, Globe, CheckCircle, Lock, Router } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import ConsultationModal from '../../components/ConsultationModal';
import DemoModal from '../../components/DemoModal';

const NetworkSecurityPage: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isConsultationModalOpen, setIsConsultationModalOpen] = useState(false);
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  const networkSolutions = [
    {
      icon: Shield,
      title: "Next-Generation Firewalls",
      description: "Advanced firewall solutions with deep packet inspection, application control, and threat prevention.",
      features: ["Application filtering", "Intrusion prevention", "SSL inspection", "Advanced threat protection"]
    },
    {
      icon: Router,
      title: "Network Segmentation",
      description: "Strategic network segmentation to limit attack surface and contain potential security breaches.",
      features: ["Micro-segmentation", "VLAN configuration", "Access control lists", "Zero-trust networking"]
    },
    {
      icon: Wifi,
      title: "Wireless Security",
      description: "Comprehensive wireless network security with enterprise-grade encryption and access controls.",
      features: ["WPA3 encryption", "Certificate-based auth", "Rogue AP detection", "Guest network isolation"]
    },
    {
      icon: Globe,
      title: "VPN & Remote Access",
      description: "Secure remote access solutions with multi-factor authentication and encrypted connections.",
      features: ["Site-to-site VPN", "Remote access VPN", "Multi-factor authentication", "Endpoint compliance"]
    }
  ];

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDarkMode ? 'bg-slate-950' : 'bg-slate-50'
    }`}>
      <Header 
        isMenuOpen={isMenuOpen} 
        setIsMenuOpen={setIsMenuOpen}
        isDarkMode={isDarkMode}
        toggleDarkMode={() => setIsDarkMode(!isDarkMode)}
      />

      <ConsultationModal 
        isOpen={isConsultationModalOpen} 
        onClose={() => setIsConsultationModalOpen(false)} 
      />
      <DemoModal 
        isOpen={isDemoModalOpen} 
        onClose={() => setIsDemoModalOpen(false)} 
      />

      {/* Hero Section */}
      <section className={`relative py-20 overflow-hidden ${
        isDarkMode 
          ? 'bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950 text-slate-100' 
          : 'bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 text-slate-900'
      }`}>
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/1181354/pexels-photo-1181354.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop"
            alt="Network Security"
            className="w-full h-full object-cover opacity-10"
          />
          <div className={`absolute inset-0 ${
            isDarkMode 
              ? 'bg-gradient-to-r from-slate-950/90 to-emerald-950/90' 
              : 'bg-gradient-to-r from-emerald-50/90 to-cyan-50/90'
          }`} />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <Wifi className={`h-16 w-16 mx-auto mb-6 ${
              isDarkMode ? 'text-emerald-400' : 'text-emerald-600'
            }`} />
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Network <span className={isDarkMode ? "text-emerald-400" : "text-emerald-600"}>Security</span>
            </h1>
            <p className={`text-xl md:text-2xl mb-8 max-w-3xl mx-auto ${
              isDarkMode ? "text-slate-300" : "text-slate-600"
            }`}>
              Protect your network infrastructure with advanced security solutions 
              including firewalls, intrusion detection, and secure remote access.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => setIsConsultationModalOpen(true)}
                className={`px-8 py-4 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center transform hover:scale-105 shadow-lg ${
                  isDarkMode 
                    ? "bg-emerald-600 hover:bg-emerald-700 text-white hover:shadow-emerald-500/25" 
                    : "bg-emerald-600 hover:bg-emerald-700 text-white hover:shadow-emerald-500/25"
                }`}
              >
                <Shield className="mr-2 h-5 w-5" />
                Network Assessment
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
              <button 
                onClick={() => setIsDemoModalOpen(true)}
                className={`px-8 py-4 rounded-lg font-semibold transition-all duration-300 border-2 ${
                  isDarkMode 
                    ? "border-emerald-400 text-emerald-400 hover:bg-emerald-400 hover:text-slate-900" 
                    : "border-emerald-600 text-emerald-600 hover:bg-emerald-600 hover:text-white"
                }`}
              >
                View Network Topology
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Network Solutions Section */}
      <section className={`py-20 ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${
              isDarkMode ? 'text-slate-100' : 'text-slate-900'
            }`}>
              Advanced Network Security Solutions
            </h2>
            <p className={`text-xl max-w-3xl mx-auto ${
              isDarkMode ? 'text-slate-300' : 'text-slate-600'
            }`}>
              Comprehensive network protection with enterprise-grade security controls
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {networkSolutions.map((solution, index) => (
              <div key={index} className={`p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow ${
                isDarkMode ? 'bg-slate-800 border border-slate-700' : 'bg-slate-50 border border-slate-200'
              }`}>
                <solution.icon className={`h-12 w-12 mb-4 ${
                  isDarkMode ? 'text-emerald-400' : 'text-emerald-600'
                }`} />
                <h3 className={`text-xl font-semibold mb-3 ${
                  isDarkMode ? 'text-slate-100' : 'text-slate-900'
                }`}>{solution.title}</h3>
                <p className={`mb-4 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  {solution.description}
                </p>
                <ul className="space-y-2">
                  {solution.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center space-x-2">
                      <CheckCircle className={`h-4 w-4 ${
                        isDarkMode ? 'text-green-400' : 'text-green-600'
                      }`} />
                      <span className={`text-sm ${
                        isDarkMode ? 'text-slate-300' : 'text-slate-600'
                      }`}>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer 
        onConsultationClick={() => setIsConsultationModalOpen(true)}
        onDemoClick={() => setIsDemoModalOpen(true)}
        isDarkMode={isDarkMode}
      />
    </div>
  );
};

export default NetworkSecurityPage;